import { DATE_FORMATS } from './constants';

// Date utility functions
export const formatDate = (date, format = DATE_FORMATS.DISPLAY, locale = 'ar-EG') => {
  const dateObj = new Date(date);
  
  switch (format) {
    case DATE_FORMATS.DISPLAY:
      return dateObj.toLocaleDateString(locale, {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    
    case DATE_FORMATS.INPUT:
      return dateObj.toISOString().split('T')[0];
    
    case DATE_FORMATS.DATETIME:
      return dateObj.toLocaleString(locale, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    
    case DATE_FORMATS.TIME:
      return dateObj.toLocaleTimeString(locale, {
        hour: '2-digit',
        minute: '2-digit'
      });
    
    default:
      return dateObj.toLocaleDateString(locale);
  }
};

export const isToday = (date) => {
  const today = new Date();
  const checkDate = new Date(date);
  return checkDate.toDateString() === today.toDateString();
};

export const isYesterday = (date) => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const checkDate = new Date(date);
  return checkDate.toDateString() === yesterday.toDateString();
};

export const isThisWeek = (date) => {
  const now = new Date();
  const checkDate = new Date(date);
  const weekStart = new Date(now.setDate(now.getDate() - now.getDay()));
  const weekEnd = new Date(now.setDate(now.getDate() - now.getDay() + 6));
  return checkDate >= weekStart && checkDate <= weekEnd;
};

export const isThisMonth = (date) => {
  const now = new Date();
  const checkDate = new Date(date);
  return checkDate.getMonth() === now.getMonth() && 
         checkDate.getFullYear() === now.getFullYear();
};

export const isThisYear = (date) => {
  const now = new Date();
  const checkDate = new Date(date);
  return checkDate.getFullYear() === now.getFullYear();
};

export const getRelativeTime = (date) => {
  const now = new Date();
  const checkDate = new Date(date);
  const diffInSeconds = Math.floor((now - checkDate) / 1000);
  
  if (diffInSeconds < 60) {
    return 'الآن';
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `منذ ${minutes} دقيقة`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `منذ ${hours} ساعة`;
  } else if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400);
    return `منذ ${days} يوم`;
  } else if (isThisYear(date)) {
    return formatDate(date, DATE_FORMATS.DISPLAY);
  } else {
    return formatDate(date, DATE_FORMATS.DISPLAY);
  }
};

export const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

export const addMonths = (date, months) => {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
};

export const addYears = (date, years) => {
  const result = new Date(date);
  result.setFullYear(result.getFullYear() + years);
  return result;
};

export const subtractDays = (date, days) => {
  return addDays(date, -days);
};

export const subtractMonths = (date, months) => {
  return addMonths(date, -months);
};

export const subtractYears = (date, years) => {
  return addYears(date, -years);
};

export const differenceInDays = (date1, date2) => {
  const oneDay = 24 * 60 * 60 * 1000;
  return Math.round(Math.abs((new Date(date1) - new Date(date2)) / oneDay));
};

export const differenceInMonths = (date1, date2) => {
  const date1Obj = new Date(date1);
  const date2Obj = new Date(date2);
  return (date2Obj.getFullYear() - date1Obj.getFullYear()) * 12 + 
         (date2Obj.getMonth() - date1Obj.getMonth());
};

export const differenceInYears = (date1, date2) => {
  return new Date(date2).getFullYear() - new Date(date1).getFullYear();
};

export const isDateValid = (date) => {
  return date instanceof Date && !isNaN(date);
};

export const parseDate = (dateString) => {
  const date = new Date(dateString);
  return isDateValid(date) ? date : null;
};

export const getStartOfDay = (date) => {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
};

export const getEndOfDay = (date) => {
  const result = new Date(date);
  result.setHours(23, 59, 59, 999);
  return result;
};

export const getStartOfWeek = (date) => {
  const result = new Date(date);
  const day = result.getDay();
  const diff = result.getDate() - day + (day === 0 ? -6 : 1);
  result.setDate(diff);
  return getStartOfDay(result);
};

export const getEndOfWeek = (date) => {
  const result = getStartOfWeek(date);
  result.setDate(result.getDate() + 6);
  return getEndOfDay(result);
};

export const getStartOfMonth = (date) => {
  const result = new Date(date);
  result.setDate(1);
  return getStartOfDay(result);
};

export const getEndOfMonth = (date) => {
  const result = new Date(date);
  result.setMonth(result.getMonth() + 1, 0);
  return getEndOfDay(result);
};

export const getStartOfYear = (date) => {
  const result = new Date(date);
  result.setMonth(0, 1);
  return getStartOfDay(result);
};

export const getEndOfYear = (date) => {
  const result = new Date(date);
  result.setMonth(11, 31);
  return getEndOfDay(result);
};

export const getAge = (birthDate) => {
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  
  return age;
};

export const getArabicDate = (dateString) => {
  if (!dateString) return 'غير متوفر';
  
  const date = new Date(dateString);
  
  return date.toLocaleDateString('ar-EG');
};

const dateUtils = {
  formatDate,
  isToday,
  isYesterday,
  isThisWeek,
  isThisMonth,
  isThisYear,
  getRelativeTime,
  addDays,
  addMonths,
  addYears,
  subtractDays,
  subtractMonths,
  subtractYears,
  differenceInDays,
  differenceInMonths,
  differenceInYears,
  isDateValid,
  parseDate,
  getStartOfDay,
  getEndOfDay,
  getStartOfWeek,
  getEndOfWeek,
  getStartOfMonth,
  getEndOfMonth,
  getStartOfYear,
  getEndOfYear,
  getAge,
  getArabicDate
};

export default dateUtils;
